import React, { useEffect, useState } from 'react';
import { getUsers, createUser, updateUser, deleteUser } from '../../api';

const isHashed = (p) => typeof p === 'string' && p.startsWith('$2');

export default function Accounts() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ code:'', name:'', role:'staff', password:'' });
  const [showCreatePw, setShowCreatePw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null); // {code, name, password, role}
  const [showPwIds, setShowPwIds] = useState(new Set()); // row IDs showing password
  const [editModal, setEditModal] = useState(null); // {id, code, name, role, password}
  const [editLoading, setEditLoading] = useState(false);
  const [editShowPw, setEditShowPw] = useState(false);
  const [editError, setEditError] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const load = () => {
    Promise.all([getUsers({role:'superadmin'}), getUsers({role:'staff'})])
      .then(([a,b]) => setUsers([...a,...b])).catch(console.error);
  };
  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const created = await createUser({...form, code: form.code.toUpperCase()});
      setCreatedInfo({ code: created.code, name: created.name, role: created.role, password: form.password });
      setForm({ code:'', name:'', role:'staff', password:'' });
      setShowCreatePw(false);
      load();
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault(); setEditError(''); setEditLoading(true);
    try {
      const body = { name: editModal.name };
      if (editModal.password) body.password = editModal.password;
      await updateUser(editModal.id, body);
      setEditModal(null);
      load();
    } catch(err) { setEditError(err.message); }
    finally { setEditLoading(false); }
  };

  const togglePwVisible = (id) => {
    setShowPwIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      alert('Copy failed — clipboard blocked');
    }
  };

  const roleColor = (r) => r === 'superadmin' ? { color:'var(--color-info)', bg:'rgba(77,159,255,0.08)', border:'rgba(77,159,255,0.25)' } : { color:'var(--accent)', bg:'rgba(22,163,74,0.10)', border:'rgba(22,163,74,0.25)' };

  return (
    <div>
      <style>{`
        .acc-input:focus, .acc-select:focus { outline:none; border-color: rgba(77,159,255,0.4) !important; box-shadow: 0 0 0 3px rgba(77,159,255,0.07) !important; }
        .add-btn-blue:hover { background: #5daeff !important; box-shadow: 0 0 16px rgba(77,159,255,0.3) !important; }
        .acc-row:hover { background: rgba(15,23,42,0.03) !important; }
        .del-btn { background: transparent; border: 1px solid rgba(220,38,38,0.20); color: rgba(220,38,38,0.40); border-radius: 5px; padding: 4px 10px; font-size: 11px; cursor: pointer; transition: all 0.18s; }
        .del-btn:hover { background: rgba(220,38,38,0.10); color: #ff5252; border-color: rgba(220,38,38,0.35); }
        .icon-btn { background: transparent; border: 1px solid rgba(15,23,42,0.10); color: var(--text-secondary); border-radius: 5px; padding: 4px 8px; font-size: 11px; cursor: pointer; transition: all 0.18s; }
        .icon-btn:hover { background: rgba(15,23,42,0.05); color: var(--text-primary); border-color: rgba(15,23,42,0.14); }
        .edit-btn:hover { background: rgba(77,159,255,0.08) !important; color: #4d9fff !important; border-color: rgba(77,159,255,0.3) !important; }
      `}</style>

      <div style={s.topbar}>
        <div>
          <div className="page-eyebrow">SETTINGS</div><h1 className="page-title">Accounts</h1>
        </div>
      </div>

      <div style={s.formCard}>
        <div style={s.formTitle}>ADD ACCOUNT</div>
        <form onSubmit={handleAdd} style={s.form}>
          <div style={s.field}>
            <label style={s.label}>USERNAME</label>
            <input className="acc-input" style={s.input} value={form.code} onChange={e=>setForm(f=>({...f,code:e.target.value}))} placeholder="username" required />
          </div>
          <div style={s.field}>
            <label style={s.label}>NAME</label>
            <input className="acc-input" style={s.input} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="John Doe" required />
          </div>
          <div style={s.field}>
            <label style={s.label}>ROLE</label>
            <select className="acc-select" style={s.input} value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
              <option value="superadmin">Super Admin</option>
              <option value="staff">Staff</option>
            </select>
          </div>
          <div style={s.field}>
            <label style={s.label}>PASSWORD</label>
            <div style={{position:'relative'}}>
              <input className="acc-input" style={{...s.input,paddingRight:36}} type={showCreatePw?'text':'password'} value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="••••••••" required />
              <button type="button" onClick={()=>setShowCreatePw(v=>!v)}
                style={{position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:14,padding:'4px 6px'}}
                title={showCreatePw?'Hide':'Show'}>
                {showCreatePw ? '🙈' : '👁'}
              </button>
            </div>
          </div>
          {error && <div style={s.error}>{error}</div>}
          <button className="add-btn-blue" style={s.addBtn} type="submit" disabled={loading}>
            {loading ? 'Adding...' : '+ Add'}
          </button>
        </form>
      </div>

      {/* Success card after creation */}
      {createdInfo && (
        <div style={s.successCard}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:'var(--color-success)',letterSpacing:1.5,textTransform:'uppercase',marginBottom:4}}>✅ Account created</div>
              <div style={{fontSize:13,color:'var(--text-secondary)'}}>Save the info to share with staff</div>
            </div>
            <button onClick={()=>setCreatedInfo(null)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:20}}>×</button>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <CredField label="USERNAME" value={createdInfo.code} onCopy={()=>copyToClipboard(createdInfo.code,'new-code')} copied={copiedId==='new-code'} />
            <CredField label="PASSWORD" value={createdInfo.password} onCopy={()=>copyToClipboard(createdInfo.password,'new-pw')} copied={copiedId==='new-pw'} mono />
          </div>
        </div>
      )}

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>{['Username','Name','Role','Password','Created','Actions'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map(u => {
              const rc = roleColor(u.role);
              const visible = showPwIds.has(u.id);
              const hashed = isHashed(u.password);
              return (
                <tr key={u.id} className="acc-row" style={s.tr}>
                  <td style={s.td}><span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontWeight:600,color:'var(--color-info)'}}>{u.code}</span></td>
                  <td style={s.td}>{u.name}</td>
                  <td style={s.td}>
                    <span style={{color:rc.color,background:rc.bg,border:`1px solid ${rc.border}`,padding:'3px 9px',borderRadius:6,fontSize:11,fontWeight:700}}>
                      {u.role === 'superadmin' ? 'Super Admin' : 'Staff'}
                    </span>
                  </td>
                  <td style={s.td}>
                    {!u.password ? (
                      <span style={{color:'var(--text-muted)',fontSize:12,fontStyle:'italic'}}>—</span>
                    ) : hashed ? (
                      <span style={{color:'var(--color-warning)',fontSize:11,fontWeight:600}} title="Old password hashed — unreadable. Click Edit to set a new one.">🔒 Reset needed</span>
                    ) : (
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <span style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:12,color:visible?'var(--text-primary)':'var(--text-muted)',letterSpacing:visible?0:2}}>
                          {visible ? u.password : '••••••••'}
                        </span>
                        <button className="icon-btn" onClick={()=>togglePwVisible(u.id)} title={visible?'Hide':'Show'}>{visible?'🙈':'👁'}</button>
                        <button className="icon-btn" onClick={()=>copyToClipboard(u.password,u.id)} title="Copy">
                          {copiedId===u.id ? '✅' : '📋'}
                        </button>
                      </div>
                    )}
                  </td>
                  <td style={{...s.td,color:'var(--text-muted)',fontSize:12}}>{new Date(u.created_at).toLocaleDateString('vi-VN')}</td>
                  <td style={s.td}>
                    <div style={{display:'flex',gap:6}}>
                      <button className="icon-btn edit-btn" onClick={()=>{ setEditModal({id:u.id,code:u.code,name:u.name,role:u.role,password:''}); setEditShowPw(false); setEditError(''); }}>
                        ✏ Edit
                      </button>
                      <button className="del-btn" onClick={()=>{ if(window.confirm(`Delete account ${u.code}?`)) deleteUser(u.id).then(load); }}>Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editModal && (
        <div style={s.overlay} onClick={()=>setEditModal(null)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:18}}>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',letterSpacing:1.5,textTransform:'uppercase',marginBottom:4}}>EDIT ACCOUNT</div>
                <div style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:14,fontWeight:700,color:'var(--color-info)'}}>{editModal.code}</div>
              </div>
              <button onClick={()=>setEditModal(null)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:22}}>×</button>
            </div>
            <form onSubmit={handleEdit}>
              <div style={{marginBottom:14}}>
                <label style={s.label}>NAME</label>
                <input className="acc-input" style={s.input} value={editModal.name} onChange={e=>setEditModal(m=>({...m,name:e.target.value}))} required />
              </div>
              <div style={{marginBottom:14}}>
                <label style={s.label}>NEW PASSWORD (leave blank to keep)</label>
                <div style={{position:'relative'}}>
                  <input className="acc-input" style={{...s.input,paddingRight:36}} type={editShowPw?'text':'password'} value={editModal.password} onChange={e=>setEditModal(m=>({...m,password:e.target.value}))} placeholder="Enter new password..." />
                  <button type="button" onClick={()=>setEditShowPw(v=>!v)}
                    style={{position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:14,padding:'4px 6px'}}>
                    {editShowPw ? '🙈' : '👁'}
                  </button>
                </div>
              </div>
              {editError && <div style={s.error}>{editError}</div>}
              <div style={{display:'flex',gap:8,marginTop:18}}>
                <button type="button" onClick={()=>setEditModal(null)} style={{...s.cancelBtn}}>Cancel</button>
                <button type="submit" disabled={editLoading} style={{...s.addBtn,flex:1,opacity:editLoading?0.5:1}}>
                  {editLoading ? 'Saving...' : '💾 Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function CredField({ label, value, onCopy, copied, mono }) {
  return (
    <div style={{background:'var(--bg-tertiary)',border:'1px solid rgba(22,163,74,0.15)',borderRadius:8,padding:'10px 12px'}}>
      <div style={{fontSize:9,fontWeight:700,color:'var(--text-muted)',letterSpacing:1,marginBottom:6}}>{label}</div>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{flex:1,fontFamily:mono?"'JetBrains Mono',monospace":"'DM Sans',sans-serif",fontSize:14,fontWeight:700,color:'var(--text-primary)',overflow:'hidden',textOverflow:'ellipsis'}}>{value}</span>
        <button onClick={onCopy} className="icon-btn">{copied?'✅ Copied':'📋 Copy'}</button>
      </div>
    </div>
  );
}

const s = {
  topbar: { marginBottom:22 },
  pageLabel: { fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:2, textTransform:'uppercase', marginBottom:4 },
  pageTitle: { fontSize:22, fontWeight:600, color:'var(--text-primary)', letterSpacing:-0.3 },
  formCard: { background:'var(--bg-tertiary)', border:'1px solid rgba(15,23,42,0.07)', borderRadius:12, padding:'20px 24px', marginBottom:20 },
  formTitle: { fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:1.8, marginBottom:16 },
  form: { display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap' },
  field: { display:'flex', flexDirection:'column', gap:6 },
  label: { fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:1, textTransform:'uppercase' },
  input: { background:'var(--bg-tertiary)', border:'1px solid rgba(15,23,42,0.08)', borderRadius:8, padding:'9px 12px', fontSize:13, color:'var(--text-primary)', transition:'all 0.18s',  },
  error: { background:'rgba(220,38,38,0.08)', color:'#b91c1c', borderLeft:'2px solid #ff5252', padding:'8px 12px', borderRadius:6, fontSize:12, marginTop:8 },
  addBtn: { background:'var(--color-info)', color:'#0a1020', border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:700, cursor:'pointer', transition:'all 0.18s',  },
  cancelBtn: { background:'transparent', color:'var(--text-secondary)', border:'1px solid rgba(15,23,42,0.10)', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer',  },
  successCard: { background:'rgba(22,163,74,0.08)', border:'1px solid rgba(22,163,74,0.25)', borderRadius:12, padding:'18px 22px', marginBottom:20 },
  tableWrap: { background:'var(--bg-tertiary)', border:'1px solid rgba(15,23,42,0.07)', borderRadius:12, overflow:'hidden' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { background:'var(--bg-tertiary)', padding:'11px 16px', fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:1, textAlign:'left', borderBottom:'1px solid rgba(15,23,42,0.07)' },
  tr: { borderBottom:'1px solid rgba(15,23,42,0.05)', transition:'background 0.15s' },
  td: { padding:'12px 16px', fontSize:13, color:'var(--text-primary)' },
  overlay: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(15,23,42,0.30)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal: { background:'var(--bg-secondary)', border:'1px solid rgba(15,23,42,0.08)', borderRadius:12, padding:24, width:'90%', maxWidth:440 },
};
